import { type ReactNode, useMemo } from 'react';

import {
    type CaregiverListWidgetModel,
    type ClientDetailWidgetModel,
    type OutreachHistoryWidgetModel,
    type ResourceShiftListWidgetModel,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_schema';
import { useAgentRunWorkspaceFileRows } from '@apps/web/hooks/use_agent_run_workspace_file_rows';

export type RetrieveArtifactModel =
    | ResourceShiftListWidgetModel
    | CaregiverListWidgetModel
    | OutreachHistoryWidgetModel
    | ClientDetailWidgetModel;

export type RetrieveArtifactView<TModel extends RetrieveArtifactModel> = {
    readonly model: TModel;
    readonly artifactHasLoaded: boolean;
    readonly isLoading: boolean;
    readonly loadError: boolean;
    readonly hasMoreRows: boolean;
    readonly isLoadingMoreRows: boolean;
    readonly loadAllRows: () => void;
};

export function RetrieveArtifactController<
    TModel extends RetrieveArtifactModel,
>({
    model,
    hydrateRows,
    render,
}: {
    readonly model: TModel;
    readonly hydrateRows: (
        model: TModel,
        rows: ReadonlyArray<unknown>,
    ) => TModel;
    readonly render: (view: RetrieveArtifactView<TModel>) => ReactNode;
}) {
    const resourceSource = model.sources.find(
        (source) => source.kind === 'resource_source',
    );
    const workspaceRows = useAgentRunWorkspaceFileRows({
        organizationId: resourceSource?.organizationId ?? '',
        runId: model.workspaceFile.runId,
        filePath: model.workspaceFile.path,
        enabled: resourceSource !== undefined,
    });
    const artifactHasLoaded = workspaceRows.rowCount !== null;
    const hydratedModel = useMemo(
        () =>
            artifactHasLoaded ? hydrateRows(model, workspaceRows.rows) : model,
        [artifactHasLoaded, hydrateRows, model, workspaceRows.rows],
    );

    return render({
        model: hydratedModel,
        artifactHasLoaded,
        isLoading: workspaceRows.isLoading,
        loadError: workspaceRows.error !== undefined,
        hasMoreRows: workspaceRows.hasMore,
        isLoadingMoreRows: workspaceRows.isLoadingMore,
        loadAllRows: workspaceRows.loadNextPage ?? (() => undefined),
    });
}
